import { useUser } from '@/contexts/UserContext';

export type CompanyType = 'general' | 'timber' | 'retail' | 'wholesale' | 'manufacturing' | 'liquid';

interface UseCompanyTypeReturn {
  companyType: CompanyType | null;
  isTimberCompany: boolean;
  isLiquidCompany: boolean;
  isManufacturingCompany: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Reads company_type from UserContext — no extra API calls.
 * UserContext already fetches /auth/me once on mount and caches it.
 */
export function useCompanyType(): UseCompanyTypeReturn {
  const { user, loading } = useUser();

  const companyType = (user?.company_type as CompanyType) ?? null;

  return {
    companyType,
    isTimberCompany: companyType === 'timber',
    isLiquidCompany: companyType === 'liquid',
    // Manufacturing gets the same production features as liquid companies,
    // just weighed in kg/g instead of litres/ml (see useLiquidUnits).
    isManufacturingCompany: companyType === 'manufacturing',
    isLoading: loading,
    error: null,
  };
}

/**
 * Units shown on the production pages. Manufacturing weighs in kg/g; every
 * other production-capable type measures volume in litres/ml. The stored
 * numbers are interchangeable (a 250ml size and a 250g size are both 250),
 * so this is a display-only swap.
 */
export function useLiquidUnits() {
  const { companyType } = useCompanyType();
  const kg = companyType === 'manufacturing';
  return {
    small: kg ? 'g' : 'ml',
    big: kg ? 'kg' : 'L',
    bigWord: kg ? 'kg' : 'litres',
    defaultContainerLabel: kg ? 'Kilograms' : 'Litres',
  };
}
